import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import AudienceGenderBar from 'components/audience/AudienceGenderBar';

import TileStandardContext from '../../context/TileStandardContext';
import TileStandardContentDetailsSection from './section/TileStandardContentDetailsSection';
import TileStandardContentDetailsChannel from './channel/TileStandardContentDetailsChannel';
import TileStandardContentDetailsPriceReco from './price-reco/TileStandardContentDetailsPriceReco';
import TileStandardContentDetailsCountries from './countries/TileStandardContentDetailsCountries';

import { __t, applyTranslations } from 'utils/language';
import { getPriceRecoPerTasksTypes } from 'utils/influencers';
import { CHANNEL_TYPE_YOUTUBE } from 'utils/channels';

import './tile-standard-content-details.scss';

import type { Channel } from 'types';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('../tile-standard-content.i18n.json'));

type Props = {
  activeChannel: Channel
};

const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'tile_standard_content';

const TileStandardContentDetails = ({
  activeChannel
}: Props): JSX.Element => {
  const content = React.useContext(TileStandardContext);
  const isYoutube = activeChannel.type === CHANNEL_TYPE_YOUTUBE;
  const channelString = __t(`${COMMON_MSG_KEY}.social.${activeChannel.type}`);
  const priceInfo = getPriceRecoPerTasksTypes(content?.channels ?? {}, activeChannel.type);
  const priceRecoLabel = __t(`${MSG_KEY}.labels.price_recommendation_${activeChannel.type}`);
  const genderDistribution = activeChannel.audienceData?.genderDistribution || [];
  const genderDistributionLabel = __t(`${MSG_KEY}.labels.gender_split`);
  const genderTopCountries = activeChannel.audienceData?.topThreeCountries || [];
  const genderTopCountriesLabel = __t(`${MSG_KEY}.labels.top_countries`);

  return (
    <div className="tile-standard-content-details">
      <div className="tile-standard-content-details__container">
        {
          (activeChannel.username || activeChannel.url) && (
            <TileStandardContentDetailsSection
              key={`tile-standard-content-details-section__channel--${channelString}`}
              label={__t(`${MSG_KEY}.labels.channel_name`, {
                channel: channelString
              })}
              labelLineHeight="small"
            >
              <TileStandardContentDetailsChannel
                activeChannel={activeChannel}
                channelString={`${__t(`${MSG_KEY}.labels.open`)} ${channelString}`}
              />
            </TileStandardContentDetailsSection>
          )
        }
        {
          !isYoutube && Boolean(priceInfo.length) && (
            <TileStandardContentDetailsSection
              key={`tile-standard-content-details-section__price-reco--${channelString}`}
              label={priceRecoLabel}
              size="small"
            >
              <TileStandardContentDetailsPriceReco
                priceInfo={priceInfo}
                isBlocked={content?.isFreemium}
              />
            </TileStandardContentDetailsSection>
          )
        }
        {
          !isYoutube && !isEmpty(genderDistribution) && (
            <TileStandardContentDetailsSection
              key={`tile-standard-content-details-section__gender-bar--${channelString}`}
              label={genderDistributionLabel}
              size="small"
            >
              <AudienceGenderBar
                items={genderDistribution}
                size="small"
                iconSize={16}
                tooltipMsg={genderDistributionLabel}
                tooltipWrapperClass="tile-standard-content-details-section__gender-bar-container"
              />
            </TileStandardContentDetailsSection>
          )
        }
        {
          !isYoutube && !isEmpty(genderTopCountries) && (
            <TileStandardContentDetailsSection
              key={`tile-standard-content-details-section__gender-countries--${channelString}`}
              label={genderTopCountriesLabel}
              size="small"
            >
              <TileStandardContentDetailsCountries
                items={genderTopCountries}
                isCollapsed={
                  activeChannel.type === 'instagram' &&
                  Boolean(content?.creator.hasActiveFbConnection)
                }
              />
            </TileStandardContentDetailsSection>
          )
        }
      </div>
    </div>
  );
};

export default TileStandardContentDetails;
